/** @format */

import {handleChange, InputWithErrorHandler, SelectWithErrorHandler} from 'frontier-form';
import {useVerifyPaymentProfile} from './use_verify_payment_profile';
import {useSavePaymentProfile} from './use_save_payment_profile';
import {PAYMENT_ACCOUNT_TYPE_ECHECK} from 'frontier-constants';
import {Drawer, DrawerHeader} from '@sttark/design-system';
import {IfElse, ErrorDisplay} from 'frontier-utilities';
import React, {useState, useEffect} from 'react';
import update from 'immutability-helper';

// styles
import './add_profile_forms.scss';

export default function (props) {
    const {onSuccessfulSave, onErrorSaving} = props;

    const [nonceData, setNonceData] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [error, setError] = useState('');

    // verifies that the payment method is valid
    const {
        isLoading: isVerifyingPaymentMethod,
        handleVerifyPaymentProfile,
        error: verifyPaymentError,
        data: verifyPaymentProfileData
    } = useVerifyPaymentProfile({
        bankData: {
            accountNumber: formData.account_number?.replaceAll(/\D/gm, ''),
            routingNumber: formData.routing_number?.replaceAll(/\D/gm, ''),
            nameOnAccount: formData.bank_name,
            accountType: formData.account_type
        }
    });

    // saves the payment method record to the database
    const {
        handleSavePaymentProfile,
        loading: isSavingPaymentProfile,
        data: savePaymentData,
        error: savePaymentError
    } = useSavePaymentProfile(formData);

    const handleChange = updateTarget => {
        setFormData(update(formData, updateTarget));
    };

    const handleClose = () => {
        setNonceData(null);
        setIsOpen(false);
        setFormData({});
        setError('');
    };

    const enhancedTriggerChild = React.cloneElement(props.children, {
        onClick: () => setIsOpen(true)
    });

    // let the parent component know that the payment method was saved
    useEffect(() => {
        if (savePaymentData) {
            onSuccessfulSave(savePaymentData);
            setIsOpen(false);
        }
    }, [savePaymentData]);

    useEffect(() => {
        if (savePaymentError) {
            if (onErrorSaving) onErrorSaving(savePaymentError);
            setError('');
        } else if (verifyPaymentError) {
            setError(verifyPaymentError);
        }
    }, [savePaymentError, verifyPaymentError]);

    useEffect(() => {
        if (verifyPaymentProfileData) {
            setNonceData(verifyPaymentProfileData);
        }
    }, [verifyPaymentProfileData]);

    // update the form data with the nonce data
    useEffect(() => {
        if (nonceData) {
            setFormData({
                ...formData,
                account_type_id: PAYMENT_ACCOUNT_TYPE_ECHECK,
                opaqueData: nonceData.opaqueData
            });
            setError('');
        }
    }, [nonceData]);

    return (
        <>
            <Drawer onClose={handleClose} className="add-credit-profile-form-03mc" isOpen={isOpen}>
                <DrawerHeader>
                    <div className="add-credit-profile-form-03mc__header d-flex align-items-center justify-content-between">
                        <h3 className="m-0 p-0">Save ACH</h3>
                        <div className="d-flex align-items-center justify-content-end gap-4">
                            <button onClick={handleClose} className="btn btn--secondary">
                                <b>Cancel</b>
                            </button>
                            <IfElse condition={isSavingPaymentProfile}>
                                <button className="btn btn--primary" disabled="disabled">
                                    <b>Saving...</b>
                                </button>
                                <button
                                    onClick={handleSavePaymentProfile}
                                    disabled={nonceData === null}
                                    className="btn btn--primary">
                                    <b>Add SCH</b>
                                </button>
                            </IfElse>
                        </div>
                    </div>
                </DrawerHeader>
                <Form formData={formData} onChange={handleChange} error={error} />
                <div>
                    <IfElse condition={!!nonceData}>
                        <aside className="alert alert-success" role="alert">
                            Payment method verified. You can save now.
                        </aside>
                        <IfElse condition={!isVerifyingPaymentMethod}>
                            <button
                                onClick={handleVerifyPaymentProfile}
                                className="p-0 m-0 color-accent-primary d-flex justify-content-start align-items-center gap-2">
                                <i className="fa fa-check" />
                                <u>Verify Payment Method</u>
                            </button>
                            <button
                                disabled="disabled"
                                className="p-0 m-0 color-structure-dark d-flex justify-content-start align-items-center">
                                <i className="fa fa-spin fa-spinner" />
                                Verifying...
                            </button>
                        </IfElse>
                    </IfElse>
                </div>
            </Drawer>
            {enhancedTriggerChild}
        </>
    );
}

const Form = handleChange(props => {
    return (
        <div className="add-credit-profile-form-03mc__form">
            <div className="d-flex align-content-center justify-content-center gap-4 mb-4">
                <InputWithErrorHandler
                    className="inspectletIgnore add-credit-profile-form-03mc__input"
                    onChange={props.onInputChange.bind(this, 'bank_name')}
                    value={props.formData.bank_name || ''}
                    placeholder="Enter Bank Name"
                    errorPath={'bank_name'}
                    error={props.error}
                    label=""
                    newestds
                />
                <InputWithErrorHandler
                    className="inspectletIgnore add-credit-profile-form-03mc__input"
                    onChange={props.onInputChange.bind(this, 'routing_number')}
                    placeholder={'Enter Routing Number'}
                    value={props.formData.routing_number || ''}
                    errorPath={'routing_number'}
                    error={props.error}
                    newestds
                    label=""
                />
            </div>
            <div className="d-flex align-content-center justify-content-center gap-4 mb-4">
                <SelectWithErrorHandler
                    options={[
                        {id: 'checking', label: 'Checking'},
                        {id: 'savings', label: 'Savings'},
                        {id: 'businessChecking', label: 'Business Checking'}
                    ]}
                    onChange={props.onInputChange.bind(this, 'account_type')}
                    placeholder="Select Account Type"
                    value={props.formData.account_type || ''}
                    errorPath={'account_type'}
                    error={props.error}
                    emptyText=" "
                    label=""
                    newestds
                />
                <InputWithErrorHandler
                    className="inspectletIgnore add-credit-profile-form-03mc__input"
                    onChange={props.onInputChange.bind(this, 'account_number')}
                    value={props.formData.account_number || ''}
                    placeholder="Enter Account Number"
                    errorPath={'account_number'}
                    error={props.error}
                    label=""
                    newestds
                />
            </div>

            <ErrorDisplay
                defaultErrorMessage="Something went wrong with the verification. Please check the form."
                className="color-alert-negative mb-4"
                displayEntityErrors={false}
                error={props.error}
                listView={true}
            />
        </div>
    );
});
